import mondaySdk from "monday-sdk-js";
import { stringify, parse } from "superjson";
import { isCIM } from "utils/url-utils";
const monday = mondaySdk();

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Helper function to get cached data from monday.storage
 * @param key - The storage key
 * @param ttl - Time to live in milliseconds
 * @returns Cached data or null if expired/not found
 */
async function getCachedData<T>(key: string, _ttl: number): Promise<T | null> {
  try {
    const result = await monday.storage.getItem(key);
    if (!result || !result.data) {
      return null;
    }

    const cacheItem = parse<CacheItem<T>>(result.data.value);
    const now = Date.now();

    // Check if cache is still valid
    if (now - cacheItem.timestamp > cacheItem.ttl) {
      // Cache expired, remove it
      await monday.storage.deleteItem(key);
      return null;
    }

    return cacheItem.data;
  } catch (error) {
    console.error("Error reading from cache:", error);
    return null;
  }
}

/**
 * Helper function to set cached data in monday.storage
 * @param key - The storage key
 * @param data - Data to cache
 * @param ttl - Time to live in milliseconds
 */
async function setCachedData<T>(key: string, data: T, ttl: number): Promise<void> {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    const result = await monday.storage.setItem(key, stringify(cacheItem));
    console.log("result", result);
  } catch (error) {
    console.error("Error writing to cache:", error);
  }
}

export const withMondayCache = async <T>(
  key: string,
  ttl: keyof typeof CACHE_DUTATIONS,
  function_to_cache: () => Promise<T>
): Promise<T> => {
  if (!isCIM()) {
    return function_to_cache();
  }
  try {
    const ttlInMilliseconds = CACHE_DUTATIONS[ttl];
    const cachedData = await getCachedData<T>(key, ttlInMilliseconds);
    if (cachedData) {
      return cachedData;
    }
    const data = await function_to_cache();
    await setCachedData(key, data, ttlInMilliseconds);
    return data;
  } catch (error) {
    console.error("Error withMondayCache:", error);
    return function_to_cache();
  }
};

export const CACHE_DUTATIONS = {
  MINUTE: 1000 * 60,
  THIRTY_MINUTES: 1000 * 60 * 30,
  ONE_HOUR: 1000 * 60 * 60,
  TWO_HOURS: 1000 * 60 * 120,
  SIX_HOURS: 1000 * 60 * 360,
  TWELVE_HOURS: 1000 * 60 * 720,
  DAY: 1000 * 60 * 60 * 24,
  WEEK: 1000 * 60 * 60 * 24 * 7,
} as const;
